import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { BooleanInput } from '@angular/cdk/coercion';
import { Subject, takeUntil, combineLatest } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { User, Trainer, Gym } from 'app/core/user/user.types';
import { UserService } from 'app/core/user/user.service';
import { TrainerService } from 'app/core/user/trainer.service';
import { GymService } from 'app/core/user/gym.service';
import { AccountModeService, AccountMode } from 'app/core/user/account-mode.service';
import { NavigationService } from 'app/core/navigation/navigation.service';

@Component({
    selector       : 'user',
    templateUrl    : './user.component.html',
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs       : 'user'
})
export class UserComponent implements OnInit, OnDestroy
{
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;
    user: User;
    trainer: Trainer | null = null;
    gym: Gym | null = null;
    currentMode: AccountMode = 'trainer';

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _translate: TranslateService,
        private _userService: UserService,
        private _trainerService: TrainerService,
        private _gymService: GymService,
        private _accountModeService: AccountModeService,
        private _navigationService: NavigationService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    get hasTrainerAccount(): boolean {
        return !!this.trainer;
    }

    get hasGymAccount(): boolean {
        return !!this.gym;
    }

    get hasBothAccounts(): boolean {
        return this.hasTrainerAccount && this.hasGymAccount;
    }

    get isInTrainerMode(): boolean {
        return this.currentMode === 'trainer';
    }

    get isInGymMode(): boolean {
        return this.currentMode === 'gym';
    }

    /**
     * Get display name based on current mode
     */
    get displayName(): string {
        if (this.isInTrainerMode && this.trainer) {
            return this.trainer.name || this.user?.email || '';
        }
        if (this.isInGymMode && this.gym) {
            return this.gym.name || this.user?.email || '';
        }
        return this.user?.name || this.user?.email || '';
    }

    /**
     * Determine what the mode button should show
     */
    get modeButtonConfig(): { show: boolean; label: string; icon: string; action: 'switch' | 'register-gym' | 'register-trainer' } {
        if (this.hasBothAccounts) {
            // User has both - show switch button
            return {
                show: true,
                label: this.isInTrainerMode 
                    ? this._translate.instant('user.switchToGym')
                    : this._translate.instant('user.switchToTrainer'),
                icon: this.isInTrainerMode ? 'heroicons_outline:office-building' : 'heroicons_outline:user',
                action: 'switch'
            };
        } else if (this.hasTrainerAccount && !this.hasGymAccount) {
            // Only trainer - offer to register as gym
            return {
                show: true,
                label: this._translate.instant('user.registerAsGym'),
                icon: 'heroicons_outline:plus',
                action: 'register-gym'
            };
        } else if (this.hasGymAccount && !this.hasTrainerAccount) {
            // Only gym - offer to register as trainer
            return {
                show: true,
                label: this._translate.instant('user.registerAsTrainer'),
                icon: 'heroicons_outline:plus',
                action: 'register-trainer'
            };
        }
        
        // No accounts yet - don't show button
        return { show: false, label: '', icon: '', action: 'switch' };
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnInit(): void
    {
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to trainer profile
        this._trainerService.trainer$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((trainer: Trainer) => {
                this.trainer = trainer;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to gym profile
        this._gymService.gym$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((gym: Gym) => {
                this.gym = gym;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to current mode
        this._accountModeService.currentMode$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((mode: AccountMode) => {
                this.currentMode = mode;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to mode changes and refresh navigation
        this._accountModeService.modeChanged$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                // Refresh navigation after mode change
                this._navigationService.get().subscribe();
            });
    }

    ngOnDestroy(): void
    {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Handle mode button click
     */
    onModeButtonClick(): void
    {
        const config = this.modeButtonConfig;
        
        switch (config.action) {
            case 'switch':
                this._accountModeService.toggleMode();
                // Navigation refresh is handled by modeChanged$ subscription
                break;
            case 'register-gym':
                this._router.navigate(['/app/gym/setup']);
                break;
            case 'register-trainer':
                this._router.navigate(['/app/profile']);
                break;
        }
    }

    /**
     * Sign out
     */
    signOut(): void
    {
        this._router.navigate(['/sign-out']);
    }
}
